import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Car,
  User as UserIcon,
  CheckCircle,
  Warning,
  ShieldCheck,
  Eraser,
  ArrowSquareOut,
  PencilSimpleLine,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";
import { toast } from "sonner";

/**
 * Public "sign the contract" page — reachable at /firmar/:token.
 * No auth required; the URL token IS the credential (30-day TTL).
 *
 * Flow:
 *   1. Load /api/public/contract/:token — show client + vehicle + terms
 *   2. Client draws signature in a canvas (touch + mouse)
 *   3. Client types their name (optional but recommended)
 *   4. POST /api/public/contract/:token/sign  { signature_data_url, typed_name }
 *   5. Confirm screen with a link to the signed PDF
 */
export default function SignContract() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [typedName, setTypedName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justSigned, setJustSigned] = useState(false);

  const load = async () => {
    setState({ loading: true, error: null, data: null });
    try {
      const { data } = await api.get(`/public/contract/${token}`);
      setState({ loading: false, error: null, data });
      if (data.receivable?.client_name) {
        setTypedName(data.receivable.client_name);
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      setState({
        loading: false,
        data: null,
        error: {
          status,
          message:
            status === 410
              ? "Este enlace de firma ha expirado."
              : status === 404
              ? "Enlace inválido o eliminado."
              : detail || "No se pudo cargar el contrato.",
        },
      });
    }
  };

  useEffect(() => { load(); }, [token]);

  const submit = async (dataUrl) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/public/contract/${token}/sign`, {
        signature_data_url: dataUrl,
        typed_name: typedName.trim() || null,
      });
      toast.success("¡Contrato firmado!");
      setJustSigned(true);
      await load();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      toast.error(detail || (status === 409 ? "Este contrato ya está firmado" : "No se pudo enviar la firma"));
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) {
    return (
      <FullScreen>
        <div className="text-center text-white/60 font-mono text-sm">Cargando contrato...</div>
      </FullScreen>
    );
  }
  if (state.error) {
    return (
      <FullScreen>
        <div className="max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center">
          <Warning size={36} weight="fill" className="mx-auto mb-2 text-red-400" />
          <div className="text-lg font-heading font-bold text-white mb-1">Enlace no disponible</div>
          <div className="text-[12px] font-mono text-white/60">{state.error.message}</div>
          <div className="text-[10px] font-mono text-white/30 mt-3">
            Pídele al vendedor que te envíe un nuevo enlace.
          </div>
        </div>
      </FullScreen>
    );
  }

  const { data } = state;
  const isSigned = data.status === "signed" || justSigned;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0B0C13] via-[#0F1017] to-[#1A1B26] pb-16">
      {/* Brand strip */}
      <header className="border-b border-white/8 bg-black/40 backdrop-blur-sm px-4 py-3 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#FACC15] flex items-center justify-center">
              <ShieldCheck size={16} weight="fill" className="text-slate-900" />
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-[#FACC15]">
                CONTRATO · FIRMA DIGITAL
              </div>
              <div className="text-[11px] font-heading font-bold text-white leading-tight">
                Revisión y firma
              </div>
            </div>
          </div>
          {isSigned && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-mono font-bold tracking-widest">
              <CheckCircle size={10} weight="fill" /> FIRMADO
            </span>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        <ContractSummary data={data} />

        {isSigned ? (
          <SignedConfirmation token={token} data={data} justSigned={justSigned} />
        ) : (
          <SignaturePad
            typedName={typedName}
            setTypedName={setTypedName}
            onSubmit={submit}
            submitting={submitting}
            clientName={data.receivable?.client_name}
          />
        )}

        <div className="text-center text-[9px] font-mono text-white/25 pt-4">
          Este enlace expira {(data.expires_at || "").slice(0, 10)} · Todo queda registrado con timestamp e IP para tu protección.
        </div>
      </main>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen w-full bg-[#0F1017] flex items-center justify-center p-4">
      {children}
    </div>
  );
}

function money(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function freq(f) {
  return { weekly: "Semanal", biweekly: "Quincenal", monthly: "Mensual" }[f] || f || "—";
}

function ContractSummary({ data }) {
  const r = data.receivable || {};
  return (
    <section
      data-testid="sign-contract-summary"
      className="rounded-2xl border border-white/10 bg-black/30 backdrop-blur-sm p-4"
    >
      <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-3">
        Contrato de venta con financiamiento
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-white/40">
            <UserIcon size={10} weight="bold" /> Cliente
          </div>
          <div className="text-[15px] font-heading font-bold text-white mt-0.5">
            {r.client_name || "—"}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-white/40">
            <Car size={10} weight="bold" /> Vehículo
          </div>
          <div className="text-[13px] font-heading font-bold text-white mt-0.5">
            {r.vehicle_year || ""} {r.vehicle_make || ""} {r.vehicle_model || ""}
          </div>
          {r.vehicle_vin && (
            <div className="text-[10px] font-mono text-white/40 mt-0.5">VIN {r.vehicle_vin}</div>
          )}
        </div>
      </div>

      <div className="border-t border-white/8 pt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px] font-mono">
        <KV label="Fecha venta" value={r.sale_date || "—"} />
        <KV label="Precio venta" value={money(r.sale_price)} />
        <KV label="Enganche" value={money(r.down_payment)} />
        <KV label="Pago pactado" value={`${money(r.agreed_payment)} · ${freq(r.payment_frequency)}`} />
        <KV label="Plazo" value={`${r.term_months || 0} pagos`} />
        <KV label="APR" value={r.apr ? `${r.apr}%` : "—"} />
        {r.first_due_date && <KV label="Primer vencimiento" value={r.first_due_date} />}
        {r.contract_number && <KV label="# Contrato" value={r.contract_number} />}
      </div>
    </section>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[8px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-white font-bold">{value}</div>
    </div>
  );
}

// ============================================================ SignaturePad
function SignaturePad({ typedName, setTypedName, onSubmit, submitting, clientName }) {
  const canvasRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const drawingRef = useRef(false);
  const lastRef = useRef({ x: 0, y: 0 });

  // Resize canvas to its CSS size on mount and on resize (device-pixel-ratio aware)
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = cv.getBoundingClientRect();
      cv.width = Math.floor(rect.width * dpr);
      cv.height = Math.floor(rect.height * dpr);
      const ctx = cv.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.lineJoin = "round";
      ctx.lineCap = "round";
      ctx.strokeStyle = "#0F1017";
      ctx.lineWidth = 2.2;
      // Repaint white background so exported PNG isn't transparent
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pointerPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t ? t.clientX : e.clientX) - rect.left,
      y: (t ? t.clientY : e.clientY) - rect.top,
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastRef.current = pointerPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pointerPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastRef.current.x, lastRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastRef.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = () => { drawingRef.current = false; };

  const clear = () => {
    const cv = canvasRef.current;
    const rect = cv.getBoundingClientRect();
    const ctx = cv.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.restore();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasInk(false);
  };

  const send = () => {
    if (!hasInk) { toast.error("Firma en el cuadro antes de enviar"); return; }
    if (!agreed) { toast.error("Debes aceptar los términos del contrato"); return; }
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSubmit(dataUrl);
  };

  return (
    <section
      data-testid="sign-pad-section"
      className="rounded-2xl border border-white/10 bg-black/30 p-4 space-y-3"
    >
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#FACC15] mb-1">
          Firma con tu dedo
        </div>
        <div className="text-[11px] font-mono text-white/50">
          Dibuja tu firma dentro del recuadro blanco. Puedes borrarla y empezar de nuevo si lo necesitas.
        </div>
      </div>

      <div className="rounded-xl border-2 border-dashed border-white/15 bg-white/5 p-1">
        <canvas
          data-testid="sign-pad-canvas"
          ref={canvasRef}
          className="w-full h-40 sm:h-48 rounded-lg bg-white touch-none cursor-crosshair"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
      </div>

      <div className="flex items-center justify-between">
        <button
          data-testid="sign-pad-clear"
          onClick={clear}
          className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white"
        >
          <Eraser size={12} weight="bold" />
          Borrar y volver a intentar
        </button>
        <span className="text-[10px] font-mono text-white/30">
          {hasInk ? "Firma capturada" : "Sin firma aún"}
        </span>
      </div>

      <div>
        <label className="block text-[10px] font-mono uppercase tracking-widest text-white/40 mb-1">
          Escribe tu nombre completo
        </label>
        <div className="relative">
          <PencilSimpleLine size={12} weight="bold" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="sign-pad-name"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={clientName || "Nombre y apellido"}
            className="w-full bg-black/40 border border-white/10 rounded-lg pl-7 pr-3 py-2 text-[13px] text-white outline-none focus:border-[#FACC15]/40"
          />
        </div>
      </div>

      <label className="flex items-start gap-2 text-[11px] font-mono text-white/70 cursor-pointer">
        <input
          data-testid="sign-pad-agree"
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-emerald-500"
        />
        <span>
          He leído y acepto los términos del contrato. Al firmar acepto los pagos, plazo y APR aquí descritos.
        </span>
      </label>

      <button
        data-testid="sign-pad-submit"
        onClick={send}
        disabled={submitting || !hasInk || !agreed}
        className="on-solid w-full flex items-center justify-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-heading font-bold py-3 text-sm"
      >
        <ShieldCheck size={16} weight="fill" />
        {submitting ? "Enviando firma..." : "Firmar y enviar"}
      </button>
    </section>
  );
}

function SignedConfirmation({ token, data, justSigned }) {
  const pdfUrl = `${API}/public/contract/${token}/pdf`;
  const when = (data.signed_at || "").slice(0, 19).replace("T", " ");
  return (
    <section
      data-testid="sign-confirmation"
      className="rounded-2xl border border-emerald-500/40 bg-emerald-500/8 p-6 text-center"
    >
      <CheckCircle size={48} weight="fill" className="mx-auto mb-3 text-emerald-400" />
      <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-1">
        {justSigned ? "Firma enviada" : "Contrato firmado"}
      </div>
      <div className="text-xl font-heading font-bold text-white">¡Listo!</div>
      <div className="text-[12px] font-mono text-white/70 mt-1">
        {data.signer_typed_name ? `Firmado por ${data.signer_typed_name}.` : "Firma recibida."}
      </div>
      {when && (
        <div className="text-[10px] font-mono text-white/40 mt-1">{when} UTC</div>
      )}
      <a
        data-testid="sign-confirmation-pdf"
        href={pdfUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 mt-4 rounded-lg border border-white/10 bg-black/30 hover:bg-black/50 text-white/80 px-4 py-2 text-[11px] font-mono font-bold uppercase tracking-widest"
      >
        <ArrowSquareOut size={12} weight="bold" />
        Descargar contrato firmado
      </a>
    </section>
  );
}
